import Client from '@spike/client-model';
import type { Form, FormInvitationItem } from '@spike/form-model';
import { FORMS_RESET, listFormInvitationItemsThunk, listFormsThunk } from '@spike/forms-action';
import { showErrorThunk } from '@spike/notifications-action';
import { useNonInitialEffect } from '@versiondos/hooks';
import ClientForms from 'components/ClientForms/ClientForms';
import { SendForms } from 'components/ClientForms/SendForms';
import Spinner from 'components/UI/Spinner';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'store';
import styles from './LeadFormsTab.module.scss';

interface LeadFormsTabProps {
    lead: Client;
    onEditLead: () => void;
    refreshSignal?: number;
}

const formsResetAction: AnyAction = { type: FORMS_RESET };

type ProbeState = 'loading' | 'empty' | 'populated' | 'error';

const LeadFormsTab: FunctionComponent<LeadFormsTabProps> = ({ lead, onEditLead, refreshSignal }) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const apiClientWrapper = useApiClientWrapper();

    const [probeState, setProbeState] = useState<ProbeState>('loading');
    const [showSendForms, setShowSendForms] = useState(false);

    const availableForms = useSelector<RootState, Array<Form>>(state => state.forms.forms);
    const probeIdRef = useRef(0);
    const mountedRef = useRef(true);
    const formsRequestedRef = useRef(false);
    const sentFromEmptyRef = useRef(false);

    const leadId = lead.id;

    const runProbe = useCallback(
        async (silent: boolean) => {
            if (leadId === undefined) {
                setProbeState('empty');
                return;
            }

            const probeId = ++probeIdRef.current;

            if (!silent) {
                setProbeState('loading');
            }

            try {
                const items: Array<FormInvitationItem> = await dispatch(
                    listFormInvitationItemsThunk(apiClientWrapper, {
                        page: 1,
                        per: 1,
                        customerId: String(leadId)
                    })
                );

                if (!mountedRef.current || probeId !== probeIdRef.current) {
                    return;
                }

                if (items.length > 0) {
                    setProbeState('populated');
                } else if (!silent) {
                    setProbeState('empty');
                }
            } catch {
                if (!mountedRef.current || probeId !== probeIdRef.current) {
                    return;
                }
                if (!silent) {
                    setProbeState('error');
                    dispatch(showErrorThunk('The forms could not be loaded. Please try again.'));
                }
            }
        },
        [leadId, dispatch]
    );

    useEffect(() => {
        mountedRef.current = true;
        void runProbe(false);

        return () => {
            mountedRef.current = false;
            dispatch(formsResetAction);
        };
    }, []);

    useNonInitialEffect(() => {
        if (probeState === 'empty' || probeState === 'error') {
            void runProbe(probeState === 'empty');
        }
    }, [refreshSignal]);

    useEffect(() => {
        if (probeState !== 'empty' || formsRequestedRef.current || availableForms.length > 0) {
            return;
        }
        formsRequestedRef.current = true;
        void dispatch(listFormsThunk(apiClientWrapper, { page: 1, per: 100 })).catch(() => undefined);
    }, [probeState, availableForms.length, dispatch]);

    if (probeState === 'loading') {
        return (
            <div className={styles.root}>
                <div className={styles.stateContainer}>
                    <Spinner />
                </div>
            </div>
        );
    }

    if (probeState === 'populated' && leadId !== undefined) {
        return (
            <div className={styles.formsContainer}>
                <ClientForms
                    clientId={leadId}
                    client={lead}
                    hideClientHeader
                    onSelect={() => onEditLead()}
                    onBook={() => undefined}
                    onNewSale={() => undefined}
                    onClose={() => undefined}
                />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Forms</h3>

                <div className={styles.card}>
                    {probeState === 'error' ? (
                        <div className={styles.errorContainer}>
                            <p className={styles.empty}>The forms could not be loaded.</p>
                            <button
                                type="button"
                                className={styles.retry}
                                onClick={() => void runProbe(false)}
                            >
                                Try Again
                            </button>
                        </div>
                    ) : (
                        <div className={styles.emptyRow}>
                            <p className={styles.empty}>There are no forms for this lead.</p>
                            <button
                                type="button"
                                className={styles.sendButton}
                                onClick={() => setShowSendForms(true)}
                            >
                                Send Form
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {showSendForms && leadId !== undefined && (
                <SendForms
                    client={lead}
                    onSent={() => {
                        sentFromEmptyRef.current = true;
                    }}
                    onClose={() => {
                        setShowSendForms(false);
                        if (sentFromEmptyRef.current) {
                            sentFromEmptyRef.current = false;

                            if (mountedRef.current) {
                                setProbeState('populated');
                            }
                        }
                    }}
                    onNavigateToClientProfile={() => onEditLead()}
                />
            )}
        </div>
    );
};

export default LeadFormsTab;
